/**
 * Encode/decode bridge between Tiptap's HTML output and the @[name](id) storage format.
 */

/**
 * Convert mention spans back to encoded format for storage.
 */
export function encodeMentionsFromHtml(html: string): string {
  // Fast path: skip innerHTML round-trip if no mentions present
  if (!html.includes("data-mention-id")) return html;

  const div = document.createElement("div");
  div.innerHTML = html;

  const mentions = div.querySelectorAll("span.mention[data-mention-id]");
  for (const mention of mentions) {
    const id = mention.getAttribute("data-mention-id") || "";
    const name = mention.getAttribute("data-mention-name") || "";
    const encoded = `@[${name}](${id})`;
    const textNode = document.createTextNode(encoded);
    mention.parentNode?.replaceChild(textNode, mention);
  }

  return div.innerHTML;
}

/**
 * Escape HTML entities to prevent XSS.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

const URL_PATTERN = /https?:\/\/[^\s<>"'`()\[\]{}]+/g;

/**
 * Format a URL for display: show hostname + path, truncated to 60 chars.
 * Input may contain HTML entities (e.g. &amp; from Tiptap output).
 */
function formatDisplayUrl(rawUrl: string): string {
  const url = rawUrl.replace(/&amp;/g, "&").replace(/&quot;/g, '"');
  try {
    const parsed = new URL(url);
    const path = parsed.pathname + parsed.search + parsed.hash;
    const short = parsed.hostname + (path === "/" ? "" : path);
    return short.length > 60 ? `${short.slice(0, 58)}…` : short;
  } catch {
    return url.length > 60 ? `${url.slice(0, 58)}…` : url;
  }
}

/**
 * Detect bare URLs in HTML text nodes and wrap them in <a> tags.
 * Skips URLs already inside <a> elements.
 */
function autoLinkUrls(html: string): string {
  return html.replace(
    /<a\b[^>]*>[\s\S]*?<\/a>|(<[^>]+>)|([^<]+)/g,
    (match, tag, text) => {
      if (tag) return tag;
      if (!text) return match;
      return text.replace(URL_PATTERN, (url: string) => {
        // Strip trailing punctuation that's likely not part of the URL
        const trailingMatch = url.match(/[.,;:!?]+$/);
        const trailing = trailingMatch?.[0] ?? "";
        const cleanUrl = trailing ? url.slice(0, -trailing.length) : url;
        const display = escapeHtml(formatDisplayUrl(cleanUrl));
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-[var(--brand-600)] underline break-words">${display}</a>${trailing}`;
      });
    },
  );
}

/**
 * Convert encoded mentions to styled spans for display,
 * and auto-link any bare URLs in the text.
 */
export function decodeMentionsToHtml(html: string): string {
  const withMentions = html.replace(
    /@\[([^\]]+)\]\(([a-f0-9-]+|everyone)\)/g,
    (_, name, id) => {
      const safeName = escapeHtml(name);
      const safeId = escapeHtml(id);
      return `<span class="mention" data-mention-id="${safeId}" data-mention-name="${safeName}" contenteditable="false">@${safeName}</span>`;
    },
  );
  return autoLinkUrls(withMentions);
}
